import React, { Component } from "react";
import { Link, Redirect } from "react-router-dom";
import { Breadcrumbs, BreadcrumbsItem } from "@sainsburys-tech/fable";
import StoreZoneSceneLevelsForm from "../StoreZoneSceneLevelsForm";
import { Event } from "../Tracking";

class StoreZoneSceneLevelsPage extends Component {
  state = {
    isValid: true,
    zoneSceneLevels: this.props.zoneSceneLevels,
    errorSubmitting: false,
    summaryErrors: [],
    notificationOpen: false,
    isSubmitting: false,
    isDirty: false
  };

  scenes = ["Trading", "Low Footfall", "Stocking", "Closed"];

  errors = {};

  fields = {
    valuePercentage: {
      name: "valuePercentage",
      rules: ["required", "validInteger", "validPercentage"]
    },
    rampLevelSeconds: {
      name: "rampLevelSeconds",
      rules: ["required", "validInteger"]
    },
    luxLevelGreaterThan: {
      name: "luxLevelGreaterThan",
      rules: ["required", "validInteger", "luxLevel"]
    }
  };

  rules = {
    required: {
      name: "required",
      valid: value => value.length > 0,
      errorMessage: "Please enter a value"
    },
    validInteger: {
      name: "validInteger",
      valid: value => Number.isInteger(Number(value)) && !value.includes("."),
      errorMessage: "Please enter a whole number"
    },
    validPercentage: {
      name: "validPercentage",
      valid: value => parseInt(value, 10) >= 0 && parseInt(value, 10) <= 100,
      errorMessage: "Please enter a value between 0 and 100"
    },
    luxLevel: {
      name: "luxLevel",
      valid: value => parseInt(value, 10) >= 0 && parseInt(value, 10) <= 2500,
      errorMessage: "Please enter a value between 0 and 2500"
    }
  };

  handleInputChange = (
    sceneName,
    field,
    inputValue,
    index = null,
    key = null
  ) => {
    this.setState({
      summaryErrors: [],
      isDirty: true
    });

    const scene = this.state.zoneSceneLevels.scenes.find(element => {
      return element.scene.name === sceneName;
    });

    let fieldName = sceneName + "." + field.name;

    if (index === null) {
      if (scene.value === null) {
        scene.value = {
          level: {}
        };
      }

      if (field.name === this.fields.valuePercentage.name) {
        scene.value.level.valuePercentage = inputValue;
      }

      if (field.name === this.fields.rampLevelSeconds.name) {
        scene.value.rampLevelSeconds = inputValue;
      }
    } else {
      fieldName = sceneName + "." + field.name + "." + key;

      if (field.name === this.fields.luxLevelGreaterThan.name) {
        scene.luxBands[index].luxLevelGreaterThan = inputValue;
      }

      if (field.name === this.fields.valuePercentage.name) {
        scene.luxBands[index].level.valuePercentage = inputValue;
      }
      if (field.name === this.fields.rampLevelSeconds.name) {
        scene.luxBands[index].rampLevelSeconds = inputValue;
      }
    }

    this.validateField(fieldName, inputValue, field.rules, key);

    this.setState({ isValid: Object.keys(this.errors).length === 0 });
  };

  validateField = (fieldName, inputValue, rules) => {
    delete this.errors[fieldName];
    for (const ruleName of rules) {
      const rule = this.rules[ruleName];
      if (!rule.valid(inputValue)) {
        this.errors[fieldName] = rule.errorMessage;
        break;
      }
    }
  };

  validateAllFields = () => {
    for (const sceneIndex in this.state.zoneSceneLevels.scenes) {
      const scene = this.state.zoneSceneLevels.scenes[sceneIndex];

      this.validateField(
        scene.scene.name + "." + this.fields.valuePercentage.name,
        scene.value.level.valuePercentage.toString(),
        this.fields.valuePercentage.rules
      );

      this.validateField(
        scene.scene.name + "." + this.fields.rampLevelSeconds.name,
        scene.value.rampLevelSeconds.toString(),
        this.fields.rampLevelSeconds.rules
      );

      for (const luxBandIndex in scene.luxBands) {
        const luxBand = scene.luxBands[luxBandIndex];

        this.validateField(
          scene.scene.name +
            "." +
            this.fields.luxLevelGreaterThan.name +
            "." +
            luxBand.key,
          luxBand.luxLevelGreaterThan.toString(),
          this.fields.luxLevelGreaterThan.rules
        );

        this.validateField(
          scene.scene.name +
            "." +
            this.fields.valuePercentage.name +
            "." +
            luxBand.key,
          luxBand.level.valuePercentage.toString(),
          this.fields.valuePercentage.rules
        );
        this.validateField(
          scene.scene.name +
            "." +
            this.fields.rampLevelSeconds.name +
            "." +
            luxBand.key,
          luxBand.rampLevelSeconds.toString(),
          this.fields.rampLevelSeconds.rules
        );
      }
    }

    this.setState({ isValid: Object.keys(this.errors).length === 0 });

    return Object.keys(this.errors).length === 0;
  };

  onFailed = () => {
    this.setState({ errorSubmitting: true, isSubmitting: false });
    this.validateAllFields();
  };

  handleValidate = () => {
    const errors = [];
    if (!this.validateAllFields()) {
      errors.push("Invalid entries");
      this.setState({ summaryErrors: errors });
      return false;
    }

    if (!this.validateLuxLevelGreaterThan()) {
      errors.push("One or more of the lux band values are the same.");
    }

    if (!this.validateLuxBandLightLevel()) {
      errors.push("One or more of the Lux Band light levels are the same.");
    }

    if (errors.length > 0) {
      this.setState({ summaryErrors: errors });
      return false;
    }
    return true;
  };

  handleSubmit = () => {
    this.setState({ errorSubmitting: false, isSubmitting: true });
    return this.props
      .upsertZoneSceneLevels(this.state.zoneSceneLevels)
      .then(response => {
        this.setState({
          notificationOpen: true,
          zoneSceneLevels: response,
          isSubmitting: false,
          isDirty: false
        });

        this.props.toastManager.add(
          "success",
          "Scene values saved",
          `Scene values for ${this.state.zoneSceneLevels.zone.name} have been saved successfully.`
        );

        this.props.onLightingStoresChange();

        Event("Scene Level Event", "Changes Saved", "(not set)");
        this.redirectToStoreZoneList();
      });
  };

  validateLuxLevelGreaterThan = () => {
    let arr = [];
    for (let i = 0; i < this.state.zoneSceneLevels.scenes.length; i++) {
      arr = [];
      for (
        let x = 0;
        x < this.state.zoneSceneLevels.scenes[i].luxBands.length;
        x++
      ) {
        arr.push(
          this.state.zoneSceneLevels.scenes[i].luxBands[
            x
          ].luxLevelGreaterThan.toString()
        );
      }
      if (this.isRepeated(arr)) {
        return false;
      }
    }

    return true;
  };

  validateLuxBandLightLevel = () => {
    const luxBandLightLevels = [];

    this.state.zoneSceneLevels.scenes.map(scene =>
      scene.luxBands.map(luxBand =>
        luxBandLightLevels.push(luxBand.level.valuePercentage.toString())
      )
    );

    if (this.isRepeated(luxBandLightLevels)) {
      return false;
    }

    return true;
  };

  isRepeated = arr => {
    const arrUnique = arr.filter((item, index) => arr.indexOf(item) >= index);
    if (arrUnique.length < arr.length) {
      return true;
    }
    return false;
  };

  clone = arr => {
    return arr.slice(0);
  };

  onAddLuxBand = sceneName => {
    const newluxBand = {
      key: this.props.createKey(),
      luxLevelGreaterThan: "",
      rampLevelSeconds: "",
      level: { valuePercentage: "" }
    };

    const zoneSceneLevelsClone = this.clone(this.state.zoneSceneLevels.scenes);
    const sceneToEdit = zoneSceneLevelsClone.find(
      scene => scene.scene.name === sceneName
    );

    if (sceneToEdit === null) {
      return;
    }

    sceneToEdit.luxBands.push(newluxBand);

    this.setState(existingState => ({
      isValid: false,
      zoneSceneLevels: {
        ...existingState.zoneSceneLevels,
        scenes: zoneSceneLevelsClone
      }
    }));
  };

  onRemoveLuxBand = (sceneName, key, rowIndex) => {
    const zoneSceneLevelsClone = this.clone(this.state.zoneSceneLevels.scenes);
    const sceneToEdit = zoneSceneLevelsClone.find(
      scene => scene.scene.name === sceneName
    );

    if (sceneToEdit === null) {
      return;
    }

    for (const field in this.fields) {
      delete this.errors[`${sceneName}.${this.fields[field].name}.${key}`];
    }
    sceneToEdit.luxBands.splice(rowIndex, 1);

    this.setState(existingState => ({
      summaryErrors: [],
      isValid: Object.keys(this.errors).length === 0,
      zoneSceneLevels: {
        ...existingState.zoneSceneLevels,
        scenes: zoneSceneLevelsClone
      }
    }));
  };

  redirectToStoreZoneList = () => {
    this.setState({ redirectToStoreZoneList: true });
  };

  render() {
    if (this.state.redirectToStoreZoneList) {
      return (
        <Redirect
          to={`/lighting/stores/${this.props.store.storeId}/commissioning/zones`}
          push={true}
        />
      );
    }
    return (
      <div>
        <div>
          <Breadcrumbs colourScheme="dark" linkAs={Link}>
            <BreadcrumbsItem href="/stores/">
              Stores
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/stores/${this.props.store.storeId}`}>
              {this.props.store.name}
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/lighting/stores/${this.props.store.storeId}/commissioning/zones`}>
              Commissioning
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/lighting/stores/${this.props.store.storeId}/zones/${this.props.zoneId}/scene-levels`} aria-current="page">
              Configure Scene Levels
            </BreadcrumbsItem>
          </Breadcrumbs>

          <h3>{this.state.zoneSceneLevels.zone.name}</h3>
          <StoreZoneSceneLevelsForm
            zoneSceneLevels={this.state.zoneSceneLevels}
            errorSubmitting={this.state.errorSubmitting}
            summaryErrors={this.state.summaryErrors}
            isSubmitting={this.state.isSubmitting}
            isValid={this.state.isValid}
            errors={this.errors}
            fields={this.fields}
            scenes={this.scenes}
            handleInputChange={this.handleInputChange}
            handleSubmit={this.handleSubmit}
            onFailed={this.onFailed}
            onValidate={this.handleValidate}
            onAddLuxBand={this.onAddLuxBand}
            onRemoveLuxBand={this.onRemoveLuxBand}
            isDirty={this.state.isDirty}
            hasLuxMeter={this.props.hasLuxMeter}
          />
        </div>
      </div>
    );
  }
}

export default StoreZoneSceneLevelsPage;
